import { ChangeDetectionStrategy, Component, Type, inject, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { ESplashLoaderComponent } from '@shared/components/loaders/e-splash-loader/e-splash-loader.component';
import { HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';

type IChingContentModule = { HdIChingPanelContentComponent: Type<unknown> };

/**
 * Dunne paneel-schil voor Classic I Ching: de echte weergave komt binnen via
 * een dynamische import zodra het paneel opengaat, zodat de I Ching-code niet
 * in de hoofdbundel zit (zelfde aanpak als het Line Companion-paneel).
 * Tot de chunk er is toont de schil een kleine loader.
 */
@Component({
  selector: 'hd-iching-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, ESplashLoaderComponent],
  styleUrl: './hd-iching-panel.component.scss',
  template: `
    @if (content(); as contentComponent) {
      <ng-container [ngComponentOutlet]="contentComponent" />
    } @else if (loadFailed()) {
      <div class="iching-panel__state" role="alert">
        <p>{{ t('humanDesign.iching.panel.loadError') }}</p>
        <button type="button" class="iching-panel__retry" (click)="retry()">
          {{ t('humanDesign.iching.panel.retry') }}
        </button>
      </div>
    } @else {
      <div class="iching-panel__state" role="status" [attr.aria-label]="t('humanDesign.iching.panel.loading')">
        <e-splash-loader [size]="40" />
      </div>
    }
  `,
})
export class HdIChingPanelComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly content = signal<Type<unknown> | undefined>(undefined);
  protected readonly loadFailed = signal(false);

  /** Overschrijfbaar in specs; de echte import levert de lazy chunk. */
  protected loadContent: () => Promise<IChingContentModule> = () => import('./hd-iching-panel-content.component');

  constructor() {
    void this.ensureContent();
  }

  protected retry(): void {
    void this.ensureContent();
  }

  protected t(key: string): string {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, undefined, this.contentLang.effectiveLang());
  }

  private async ensureContent(): Promise<void> {
    this.loadFailed.set(false);
    try {
      const contentModule = await this.loadContent();
      this.content.set(contentModule.HdIChingPanelContentComponent);
    } catch {
      this.loadFailed.set(true);
    }
  }
}
